import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { useDispatch } from 'react-redux';
import { strings } from '../../resources/strings';
import { orderTags } from '../../resources/orderResources';
import { omnichannelResources } from '../../resources/omnichannelResources';
import { MainActions } from '../../actions/MainActions';
import { Field, FieldType } from '../../common-components/Field/Field';
import { Button } from '../../common-components/Button/Button';
import { If } from '../../common-components/If/If';
import { SeparationType } from '../../enums/SeparationType';
import { OrderAlertType } from '../../enums/OrderAlertType';
import { FramesQuantity } from '../../enums/FramesQuantity';
import { ProfileType } from '../../enums/ProfileType';
import { SessionHelper } from '../../helpers/SessionHelper';
import { orderStates } from '../../services/StatesOrders/StatesService';
import { prescriptionStates } from '../../services/StatesPrescriptions/StatesPrescriptionsService';
import { Seller } from '../../models/Seller';
import { ShopLocation } from '../../models/ShopLocation';
import { PrescriptionLens } from '../../models/PrescriptionLens';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { CheckList, CheckListField, CheckListItem, ContainerBody, Row } from './styles';

export interface IFilterToListOrdersOutput {
  customerName?: string;
  customerEmail?: string;
  specialTag?: string;
  number?: number;
  numbers?: string;
  stateIds?: string;
  statePrescriptionId?: number;
  statePrescriptionIds?: string;
  startDate?: Date | null;
  endDate?: Date | null;
  orderAlertType?: OrderAlertType;
  locationId?: number | null;
  locationName?: string | null;
  sellerId?: number | null;
  sellerName?: string | null;
  stateIdsForUserProfile?: string;
  deliveryDeadlineStartDate?: Date | null;
  deliveryDeadlineEndDate?: Date | null;
  deliveryStartDate?: Date | null;
  deliveryEndDate?: Date | null;
  lensMaterialName?: string | null;
  lensClassName?: string | null;
  fulfilledBy?: string | null;
  originedBy?: string | null;
  ignoreDefaultUserProfileFilters?: boolean | null;
  salesTypeFilter?: string | null;
  framesQuantity?: FramesQuantity;
}

// estados de grau aplicados: lista separada por espaço (Admin/Ecommerce) ou um único id (demais perfis)
export function getAppliedStatePrescriptionIds(values?: IFilterToListOrdersOutput): number[] {
  const ids = (values?.statePrescriptionIds || '').split(' ').map(id => +id).filter(id => id > 0);
  if (ids.length > 0) return ids;
  return values?.statePrescriptionId && values.statePrescriptionId > 0 ? [+values.statePrescriptionId] : [];
}

interface IModalFiltersToListOrdersProps {
  appliedFilters?: IFilterToListOrdersOutput;
  separationType?: SeparationType;
  close: () => void;
  finish: (values: IFilterToListOrdersOutput) => void;
}

const ModalFiltersToListOrders: React.FC<IModalFiltersToListOrdersProps> = ({
  appliedFilters,
  separationType,
  close,
  finish
}) => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const { userLocations, session, mainLocation } = useAuth();
  const canFilterManyPrescriptionStates = SessionHelper.canFilterManyPrescriptionStates(session);
  const [sellers, setSellers] = React.useState<Seller[]>([]);
  const [sellersWaiting, setSellersWaiting] = React.useState<boolean>(false);
  const [prescriptionLenses, setPrescriptionLenses] = React.useState<PrescriptionLens[]>([]);
  const [loadingPrescriptionLenses, setLoadingPrescriptionLenses] = React.useState<boolean>(false);
  const [orderNumbers, setOrderNumbers] = React.useState<string | undefined>(appliedFilters?.numbers);
  const [startDate, setStartDate] = React.useState<Date | null>(appliedFilters?.startDate || null);
  const [endDate, setEndDate] = React.useState<Date | null>(appliedFilters?.endDate || null);
  const [stateIds, setStateIds] = React.useState<string | undefined>(appliedFilters?.stateIds);
  const [statePrescriptionId, setStatePrescriptionId] = React.useState<number | undefined>(appliedFilters?.statePrescriptionId);
  const [statePrescriptionIds, setStatePrescriptionIds] = React.useState<number[]>(getAppliedStatePrescriptionIds(appliedFilters));
  const [customerName, setCustomerName] = React.useState<string | undefined>(appliedFilters?.customerName);
  const [customerEmail, setCustomerEmail] = React.useState<string | undefined>(appliedFilters?.customerEmail);
  const [specialTag, setSpecialTag] = React.useState<string | undefined>(appliedFilters?.specialTag);
  const [orderAlertType, setOrderAlertType] = React.useState<OrderAlertType>(appliedFilters?.orderAlertType || OrderAlertType.NoAlert);
  const [locationId, setLocationId] = React.useState<number | undefined | null>(appliedFilters?.locationId);
  const [locationName, setLocationName] = React.useState<string | undefined | null>(appliedFilters?.locationName);
  const [locations, setLocations] = React.useState<ShopLocation[]>([]);
  const [loadingLocations, setLoadingLocations] = React.useState<boolean>(true);
  const [sellerId, setSellerId] = React.useState<number | undefined | null>(appliedFilters?.sellerId);
  const [sellerName, setSellerName] = React.useState<string | undefined | null>(appliedFilters?.sellerName);
  const [stateIdsForUserProfile, setStateIdsForUserProfile] = React.useState<string | undefined>(appliedFilters?.stateIdsForUserProfile);
  const [deliveryDeadlineStartDate, setDeliveryDeadlineStartDate] = React.useState<Date | null>(appliedFilters?.deliveryDeadlineStartDate || null);
  const [deliveryDeadlineEndDate, setDeliveryDeadlineEndDate] = React.useState<Date | null>(appliedFilters?.deliveryDeadlineEndDate || null);
  const [deliveryStartDate, setDeliveryStartDate] = React.useState<Date | null>(appliedFilters?.deliveryStartDate || null);
  const [deliveryEndDate, setDeliveryEndDate] = React.useState<Date | null>(appliedFilters?.deliveryEndDate || null);
  const [lensMaterialName, setLensMaterialName] = React.useState<string | undefined | null>(appliedFilters?.lensMaterialName || null);
  const [lensClassName, setLensClassName] = React.useState<string | undefined | null>(appliedFilters?.lensClassName || null);
  const [fulfilledBy, setFulfilledBy] = React.useState<string | undefined | null>(appliedFilters?.fulfilledBy);
  const [originedBy, setOriginedBy] = React.useState<string | undefined | null>(appliedFilters?.originedBy);
  const [ignoreDefaultUserProfileFilters, setIgnoreDefaultUserProfileFilters] = React.useState<boolean | undefined | null>(appliedFilters?.ignoreDefaultUserProfileFilters);
  const [salesTypeFilter, setSalesTypeFilter] = React.useState<string | undefined | null>(appliedFilters?.salesTypeFilter);
  const [framesQuantity, setFramesQuantity] = React.useState<FramesQuantity>(appliedFilters?.framesQuantity || FramesQuantity.All);

  const {
    getActiveSellersByLocationsService,
    statesService,
    getLocationListService,
    getPrescriptionLensesService
  } = React.useContext(ServicesContext);

  React.useEffect(() => {
    getSellers();
    getLocations();
    getPrescriptionLenses();
  }, []);

  const sellerFilter = React.useMemo(() => {
    if (!sellers || sellers.length <= 0) {
      if (sellersWaiting) {
        return (
          <Row>
            <Field
              type={FieldType.Select}
              label={strings.Seller}
              selectProps={{
                options: [
                  { id: 0, name: 'Carregando...' },
                ],
                displayField: 'name',
                keyField: 'id'
              }}
              value={0}
              marginTop={'0px'}
              disabled={true}
            />
          </Row>
        );
      }
      return null;
    }
    return (
      <Row>
        <Field
          type={FieldType.Select}
          label={strings.Seller}
          selectProps={{
            options: [
              { id: 0, name: strings.All },
              ...sellers
            ],
            displayField: 'name',
            keyField: 'id'
          }}
          value={sellerId}
          change={(value) => setSeller(sellers.find(s => s.id === +value))}
          marginTop={'0px'}
        />
      </Row>
    );
  }, [sellerId, sellers, sellerName, appliedFilters, sellersWaiting]);

  const lensMaterialFilter = React.useMemo(() => {
    if (!(separationType === SeparationType.Prescription && SessionHelper.canFilterLensMaterial(session))) {
      return null;
    }

    const options: { id: string | null; name: string }[] = [];
    if (loadingPrescriptionLenses) {
      options.push({ id: '', name: 'Carregando...' });
    } else {
      options.push({ id: null, name: strings.All });
      prescriptionLenses.forEach(pl => {
        // verificar se user profile está em userFilterProfiles (string com profileType separados por vírgula)
        // se userFilterProfiles for null ou empty ou session?.user?.profileType estiver em userFilterProfiles, mostrar a lente
        const userProfileType = String(session?.user?.profileType);
        const userFilterProfiles = pl.userFilterProfiles?.split(',').map(p => p.trim()) || [];
        if (userFilterProfiles.length > 0 && !userFilterProfiles.includes(userProfileType)) {
          return;
        }
        // verifica se a opção já foi adicionada
        if (options.find(o => o.id === pl.className)) {
          return;
        }
        options.push({ id: pl.className, name: pl.className });
      });
      options.push({ id: 'Lentes Variadas', name: 'Lentes Variadas' });
    }

    return (
      <Row>
        <Field
          type={FieldType.Select}
          label={strings.LensMaterial}
          selectProps={{
            options: options,
            displayField: 'name',
            keyField: 'id'
          }}
          value={lensClassName}
          change={(value) => setLensClassName(value)}
          marginTop={'0px'}
          disabled={loadingPrescriptionLenses}
        />
      </Row>
    );
  }, [loadingPrescriptionLenses, prescriptionLenses, lensClassName, separationType, session]);

  const filterStateOrderRender = SessionHelper.canFilterStates(session) && (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.StateOfOrder}
        selectProps={{
          options: [
            { id: 0, name: strings.All },
            ...statesService.translateListByUserProfile(statesService.filterByUserProfile(orderStates, session?.user?.profileType), session?.user?.profileType)
          ],
          displayField: 'name',
          keyField: 'id'
        }}
        value={stateIds ? stateIds.split(' ')[0] : undefined}
        change={(value) => setStateIds(value)}
        marginTop={'0px'}
      />
    </Row>
  );

  const filterStatePrescriptionRender = !SessionHelper.canFilterPrescriptionStates(session) || separationType !== SeparationType.Prescription ? null
    : canFilterManyPrescriptionStates ? (
    <Row>
      <CheckListField>
        <label className='form-label'>{strings.StateOfDegree}</label>
        <CheckList className='form-control'>
          {prescriptionStates.map(state => (
            <CheckListItem key={state.id}>
              <input
                type='checkbox'
                className='form-check-input'
                checked={statePrescriptionIds.includes(state.id)}
                onChange={(e) => changeStatePrescriptionIds(state.id, e.target.checked)}
              />
              {state.description}
            </CheckListItem>
          ))}
        </CheckList>
      </CheckListField>
    </Row>
  ) : (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.StateOfDegree}
        selectProps={{
          options: [{ id: 0, description: strings.All }, ...prescriptionStates],
          displayField: 'description',
          keyField: 'id'
        }}
        value={statePrescriptionId}
        change={(value) => setStatePrescriptionId(value)}
        marginTop={'0px'}
      />
    </Row>
  );

  const filterStateOrderForUserProfileRender = (session?.user?.profileType === ProfileType.GlobalPicker) &&
    (
      <Row>
        <Field
          type={FieldType.Select}
          label={strings.StateOfOrder}
          selectProps={{
            options: [
              { id: 0, name: strings.All },
              ...(statesService.getByUserProfileAndSeparationType(session, separationType) || [])
            ],
            displayField: 'name',
            keyField: 'id'
          }}
          value={stateIdsForUserProfile ? stateIdsForUserProfile.split(' ')[0] : undefined}
          change={(value) => setStateIdsForUserProfile(value)}
          marginTop={'0px'}
        />
      </Row>
    );

  const filterSpecialTagAndAlertRender = (SessionHelper.canFilterSpecialTagAndAlert(session)) && (
    <Row>
      <Field
        type={FieldType.Select}
        label={strings.SpecialTag}
        selectProps={{
          options: [
            { id: '', name: strings.All },
            { id: orderTags.influenciadores.value, name: orderTags.influenciadores.display },
            { id: orderTags.amaro.value, name: orderTags.amaro.display },
            { id: orderTags.non_progressive_order_bf2023.value, name: orderTags.non_progressive_order_bf2023.display },
            { id: orderTags.externally_changed.value, name: orderTags.externally_changed.display },
            { id: orderTags.pre_sale_sport.value, name: orderTags.pre_sale_sport.display }
          ],
          displayField: 'name',
          keyField: 'id'
        }}
        value={specialTag}
        change={(value) => setSpecialTag(value)}
        marginTop={'0px'}
        marginRight='5px'
      />
    </Row>
  );

  return (
    <Modal
      show={true}
      onHide={() => close()}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header style={{ height: '50px' }}>
        <Modal.Title>
          Filtrar Pedidos
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '75vh', overflowY: 'auto', overflowX: 'hidden' }}>
        <ContainerBody>
          <Row>
            <Field
              type={FieldType.Text}
              label={strings.Orders}
              value={orderNumbers}
              change={(value) => changeOrderNumbers(value)}
              replaceEnterCodeByValue={' '}
              autoFocus={true}
              marginTop={'0px'}
            />
          </Row>

          <If condition={session?.user?.profileType === ProfileType.Vendor}>
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.FulfilledBy}
                selectProps={{
                  options: [
                    {
                      id: omnichannelResources.FulfillmentGroupOptions.MyStore.filterId,
                      name: omnichannelResources.FulfillmentGroupOptions.MyStore.title
                    },
                    {
                      id: omnichannelResources.FulfillmentGroupOptions.MyStoreAndEcommerce.filterId,
                      name: omnichannelResources.FulfillmentGroupOptions.MyStoreAndEcommerce.title
                    },
                    {
                      id: omnichannelResources.FulfillmentGroupOptions.Ecommerce.filterId,
                      name: omnichannelResources.FulfillmentGroupOptions.Ecommerce.title
                    }
                  ],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={fulfilledBy}
                change={(value) => setFulfilledBy(value)}
                marginTop={'0px'}
              />
            </Row>
          </If>

          <If condition={session?.user?.profileType === ProfileType.Ecommerce}>
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.OriginatedBy}
                selectProps={{
                  options: [
                    {
                      id: omnichannelResources.OrderOriginGroupOptions.EcommerceAndPhysicalStores.filterId,
                      name: omnichannelResources.OrderOriginGroupOptions.EcommerceAndPhysicalStores.title
                    },
                    {
                      id: omnichannelResources.OrderOriginGroupOptions.Ecommerce.filterId,
                      name: omnichannelResources.OrderOriginGroupOptions.Ecommerce.title
                    },
                    {
                      id: omnichannelResources.OrderOriginGroupOptions.PhysicalStores.filterId,
                      name: omnichannelResources.OrderOriginGroupOptions.PhysicalStores.title
                    }
                  ],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={originedBy}
                change={(value) => setOriginedBy(value)}
                marginTop={'0px'}
              />
            </Row>
          </If>

          <If condition={userLocations.length > 1 && !loadingLocations}>
            <Row>
              <Field
                type={FieldType.Select}
                label={strings.Location}
                selectProps={{
                  options: [{ id: 0, name: strings.All }, ...locations.filter(l => userLocations.some(ulid => ulid === l.id))],
                  displayField: 'name',
                  keyField: 'id'
                }}
                value={locationId}
                change={(value) => setLocation(locations.find(l => l.id === +value))}
                marginTop={'0px'}
              />
            </Row>
          </If>
          <Row>
            <Field
              type={FieldType.DatePicker}
              label={strings.PaidOrderOf}
              marginRight='5px'
              value={startDate}
              change={(value) => setStartDate(value)}
              marginTop={'0px'}
            />
            <Field
              type={FieldType.DatePicker}
              label={strings.PaidOrderUntil}
              marginLeft='5px'
              value={endDate}
              change={(value) => setEndDate(value)}
              marginTop={'0px'}
            />
          </Row>
          {SessionHelper.canFilterCustomerDeadline(session) ?
            <Row>
              <Field
                type={FieldType.DatePicker}
                label={strings.CustomerDeadlineOf}
                marginRight='5px'
                value={deliveryDeadlineStartDate}
                change={(value) => setDeliveryDeadlineStartDate(value)}
                marginTop={'0px'}
              />
              <Field
                type={FieldType.DatePicker}
                label={strings.CustomerDeadlineUntil}
                marginLeft='5px'
                value={deliveryDeadlineEndDate}
                change={(value) => setDeliveryDeadlineEndDate(value)}
                marginTop={'0px'}
              />
            </Row>
            : null}
          {SessionHelper.canFilterDeliveredOrder(session) ?
            <Row>
              <Field
                type={FieldType.DatePicker}
                label={strings.DeliveredOf}
                marginRight='5px'
                value={deliveryStartDate}
                change={(value) => setDeliveryStartDate(value)}
                marginTop={'0px'}
              />
              <Field
                type={FieldType.DatePicker}
                label={strings.DeliveredUntil}
                marginLeft='5px'
                value={deliveryEndDate}
                change={(value) => setDeliveryEndDate(value)}
                marginTop={'0px'}
              />
            </Row>
            : null}
          {filterStateOrderRender}
          {filterStatePrescriptionRender}
          {filterStateOrderForUserProfileRender}
          {SessionHelper.canFilterCustomer(session) ?
            <Row>
              <Field
                type={FieldType.Text}
                label={strings.Client}
                value={customerName}
                change={(value) => setCustomerName(value)}
                marginTop={'0px'}
                marginRight='5px'
              />
              <Field
                type={FieldType.Text}
                label={strings.Email}
                value={customerEmail}
                change={(value) => setCustomerEmail(value)}
                marginTop={'0px'}
                marginLeft='5px'
              />
            </Row>
            : null}
          {filterSpecialTagAndAlertRender}
          {sellerFilter}
          {lensMaterialFilter}

          <Row>
            <Field
              type={FieldType.Select}
              label={strings.SalesType}
              selectProps={{
                options: [
                  { id: '', name: strings.All },
                  { id: 'pre-sale', name: strings.PreSale },
                  { id: 'sale', name: strings.Sale }
                ],
                displayField: 'name',
                keyField: 'id'
              }}
              value={salesTypeFilter || ''}
              change={(value) => setSalesTypeFilter(value)}
              marginTop={'0px'}
            />
          </Row>

          <Row>
            <Field
              type={FieldType.Select}
              label={strings.FramesQuantity}
              selectProps={{
                options: [
                  { id: FramesQuantity.All, name: strings.All },
                  { id: FramesQuantity.ManyOnly, name: strings.ManyFramesOnly },
                  { id: FramesQuantity.OneOnly, name: strings.OneFrameOnly }
                ],
                displayField: 'name',
                keyField: 'id'
              }}
              value={framesQuantity}
              change={(value) => setFramesQuantity(+value)}
              marginTop={'0px'}
            />
          </Row>

          {session?.userProfileConfig?.considerDefaultFilters && separationType == SeparationType.Prescription ? (
            <Row>
              <Field
                type={FieldType.CheckBox}
                label={'Ignorar filtros padrão'}
                value={ignoreDefaultUserProfileFilters}
                change={(value) => setIgnoreDefaultUserProfileFilters(value)}
                marginTop={'0px'}
              />
            </Row>
          ) : null}
        </ContainerBody>
      </Modal.Body>
      <Modal.Footer>
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Button text={strings.Cancel} click={() => close()} marginRight={'10px'} />
          <Button text={strings.Confirm} click={() => filter()} marginLeft={'10px'} />
        </div>
      </Modal.Footer>
    </Modal>
  );

  function changeOrderNumbers(value: string) {
    // trocar \n ou \r por espaço em branco e remover espaços em branco extras
    const newValue = value.replace(/\n/g, ' ').replace(/\r/g, ' ').replace(/\s+/g, ' ').trim();
    setOrderNumbers(newValue);
  }

  function changeStatePrescriptionIds(id: number, checked: boolean) {
    setStatePrescriptionIds(ids => checked
      ? [...ids.filter(x => x !== id), id]
      : ids.filter(x => x !== id));
  }

  function filter() {
    finish({
      numbers: orderNumbers,
      startDate: startDate,
      endDate: endDate,
      stateIds: stateIds,
      statePrescriptionId: canFilterManyPrescriptionStates ? undefined : statePrescriptionId,
      statePrescriptionIds: canFilterManyPrescriptionStates && statePrescriptionIds.length > 0 ? statePrescriptionIds.join(' ') : undefined,
      customerName: customerName,
      customerEmail: customerEmail,
      specialTag: specialTag,
      orderAlertType: +orderAlertType,
      locationId: locationId,
      locationName: locationName,
      sellerId: sellerId,
      sellerName: sellerName,
      stateIdsForUserProfile: stateIdsForUserProfile,
      deliveryDeadlineStartDate: deliveryDeadlineStartDate,
      deliveryDeadlineEndDate: deliveryDeadlineEndDate,
      deliveryStartDate: deliveryStartDate,
      deliveryEndDate: deliveryEndDate,
      lensMaterialName: lensMaterialName,
      lensClassName: lensClassName,
      fulfilledBy: fulfilledBy,
      originedBy: originedBy,
      ignoreDefaultUserProfileFilters: ignoreDefaultUserProfileFilters,
      salesTypeFilter: salesTypeFilter || null,
      framesQuantity: framesQuantity,
    });
  }

  function getSellers() {
    if (!SessionHelper.canFilterSeller(session)) return;
    setSellersWaiting(true);
    mainActions.setNotWait(true);
    getActiveSellersByLocationsService.handle(userLocations.join(','))
      .then(result => {
        setSellers(result?.value?.sellers || []);
        setSellersWaiting(false);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
      });
  }

  function getPrescriptionLenses() {
    setLoadingPrescriptionLenses(true);
    mainActions.setNotWait(true);
    getPrescriptionLensesService.handle()
      .then(result => {
        setPrescriptionLenses(result?.value?.prescriptionLenses || []);
      })
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingPrescriptionLenses(false);
      });
  }

  function setSeller(seller?: Seller | null) {
    setSellerId(seller?.id);
    setSellerName(seller?.name);
  }

  function getLocations() {
    const manyLocations = userLocations.length > 1;
    if (!manyLocations) return;
    setLoadingLocations(true);
    mainActions.setNotWait(true);
    getLocationListService.handle()
      .then(result => setLocations(result?.value?.locations || []))
      .finally(() => {
        setTimeout(() => mainActions.setNotWait(false), 1000);
        setLoadingLocations(false);
      });
  }

  function setLocation(location?: ShopLocation | null) {
    setLocationId(location?.id);
    setLocationName(location?.name);
  }
}

export { ModalFiltersToListOrders };